<template>
  <div class="dashboard-group">
    <section>
      <header class="p-4" v-if="!currentGroup._id">
        <div class="header-wrapper">
          <div class="header-title">
            <h2>{{ labels.myGroups }}</h2>
            <small>{{ labels.manageYourGroups }}</small>
          </div>
          <div class="header-actions">
            <b-button
              v-if="!$can('access', 'read-only')"
              variant="tertiary"
              @click="$emit('openGroupsImporterModal')"
              class="mr-2 mb-2"
              :title="labels.import"
              :disabled="!$can('create', 'group') || isBasicPlan"
            >
              <i class="material-icons">
                file_upload
              </i>
              <span class="ml-2">
                {{ labels.import }}
              </span>
            </b-button>
            <b-button
              v-if="!$can('access', 'read-only')"
              variant="tertiary"
              v-b-modal.modalGroups
              class="mb-2"
              :title="labels.newGroup"
              :disabled="!$can('create', 'group')"
            >
              <i class="mdi mdi-account-multiple-plus"></i>
              <span class="pl-1">
                {{ labels.newGroup }}
              </span>
            </b-button>
          </div>
        </div>
      </header>
      <div
        :class="{
          'users-list': true,
          'inactive-group-header': currentGroup.inactive,
        }"
      >
        <div class="user-list-wrap" v-if="currentGroup._id">
          <header>
            <b-button
              variant="outline-primary"
              class="mdi mdi-chevron-left btn btn-options"
              @click="onBreadcrumbClick(breadcrumb[breadcrumb.length - 2])"
            >
            </b-button>
            <div class="title-wrap">
              <div class="group-breadcrumb" v-if="breadcrumb">
                <div
                  v-for="bread in breadcrumb.slice(0, breadcrumb.length - 1)"
                  :key="bread._id"
                >
                  <div class="bread" @click="onBreadcrumbClick(bread)">
                    {{ bread.name }}
                  </div>
                  <i class="mdi mdi-chevron-right"></i>
                </div>
              </div>
              <h2>
                {{ currentGroup.name }}
                <GroupsInactive v-if="currentGroup.inactive" />
              </h2>
              <small
                >{{ currentGroup.usersCountTotal }}
                {{ labels.membersInThisGroup }}</small
              >
            </div>
            <div v-if="$can('edit', 'group')">
              <b-button
                variant="primary"
                @click="$emit('groupEditClick', currentGroup)"
                class="mr-3 edit-btn"
              >
                <i class="mdi mdi-pencil d-inline-block"></i>
                <span class="d-none d-lg-inline-block pl-1">
                  {{ labels.editGroups }}
                </span>
              </b-button>
            </div>
          </header>
          <section>
            <div class="managers d-flex pl-3 pr-1">
              <div class="d-flex buttons-wrapper">
                <b-button
                  class="carousel__backward"
                  variant="link"
                  :disabled="!carousel.canAdvanceBackward"
                  @click="navigationCarousel('backward')"
                >
                  <i class="material-icons" @click.native.stop="">
                    keyboard_arrow_left
                  </i>
                </b-button>
                <span class="carousel__slide__text">{{ labels.managers }}</span>
              </div>
              <div class="carousel__wrapper">
                <b-row no-gutters>
                  <b-col sm="12">
                    <carousel
                      class="carousel mx-2"
                      ref="managerCarousel"
                      :perPageCustom="[
                        [0, 1],
                        [760, 2],
                        [990, 1],
                        [1000, 2],
                        [1200, 3],
                      ]"
                      :paginationEnabled="false"
                      @mounted="onCarouselMounted"
                    >
                      <slide
                        :class="{
                          carousel__slide: true,
                          'px-2': true,
                          'label-inactive': user.inactive,
                        }"
                        v-for="user in currentGroup.managers"
                        :key="user._id"
                      >
                        <i class="material-icons carousel__slide__icon"
                          >account_circle</i
                        >
                        <span class="carousel__slide__text text-truncate">
                          {{
                            user.profile.firstname + ' ' + user.profile.lastname
                          }}
                        </span>
                      </slide>
                    </carousel>
                  </b-col>
                </b-row>
              </div>
              <div class="buttons-wrapper buttons-wrapper__forward">
                <b-button
                  class="carousel__forward"
                  variant="link"
                  :disabled="!carousel.canAdvanceForward"
                  @click="navigationCarousel('foward')"
                >
                  <i class="material-icons" @click.native.stop="">
                    keyboard_arrow_right
                  </i>
                </b-button>
              </div>
            </div>
            <ul class="members">
              <li
                :class="{
                  'user-item-list': true,
                  'label-inactive': user.inactive,
                }"
                v-for="user in groupMembers"
                :key="user._id"
              >
                <i class="image mdi mdi-account-circle"></i>
                <div class="user-title">
                  <span class="name">
                    {{ user.profile.firstname + ' ' + user.profile.lastname }}
                  </span>
                  <br />
                  <span class="email">{{ user.email }}</span> <br />
                  <template v-if="$can('see', 'user.participation')">
                    <div class="role" v-if="user.hasOwnProperty('unconfirmed')">
                      Status:
                      <span v-if="user.inactive" class="inactive">
                        {{ labels.inactiveUser }}
                      </span>
                      <span v-else-if="user.unconfirmed" class="invited">
                        {{ labels.invited }}
                      </span>
                      <span v-else>
                        {{ labels.confirmed }}
                      </span>
                    </div>
                  </template>
                </div>
              </li>
            </ul>
          </section>
        </div>
      </div>
      <div class="group-list row m-0">
        <div
          class="col-md-6 col-sm-12 p-2 collumn-group"
          v-for="group in groups"
          :key="group.id"
        >
          <CardGroup
            :group="group"
            @groupEntryClick="$emit('groupEntryClick', $event)"
            @editClick="$emit('groupEditClick', $event)"
            @groupRemoveClick="$emit('groupRemoveClick', $event)"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CardGroup from './CardGroup.vue'
import { removeFakeEmail } from '@/helpers/mailFake'
import { Carousel, Slide } from 'vue-carousel'
import { juxt } from 'ramda'
import GroupsInactive from '@/components/Groups/GroupsInactive.vue'
import { isBasicPlan } from '@/service/user'

const props = {
  groups: {
    type: Array,
    required: false,
  },
}

export default {
  props: props,
  components: {
    CardGroup,
    Carousel,
    Slide,
    GroupsInactive,
  },
  data() {
    return {
      carousel: {
        canAdvanceBackward: false,
        canAdvanceForward: false,
      },
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      currentGroup: 'groups/currentGroup',
      breadcrumb: 'groups/breadcrumb',
    }),

    labels() {
      return {
        ...this.resources.page.home.groups,
        ...this.resources.page.home.groups.dashboard,
      }
    },

    groupMembers() {
      return this.currentGroup.members.map(member => ({
        ...member,
        email: removeFakeEmail(member.email),
      }))
    },

    isBasicPlan() {
      return isBasicPlan()
    },
  },

  methods: {
    onCarouselMounted() {
      juxt([this.setCarouselProperty, this.addWatchOnCarousel])(
        this.$refs.managerCarousel
      )
    },
    onBreadcrumbClick(group) {
      this.$emit('breadcrumbClick', group)
    },
    navigationCarousel(direction) {
      this.$refs.managerCarousel.handleNavigation(direction)
    },
    addWatchOnCarousel(carousel) {
      carousel.$watch('canAdvanceBackward', () =>
        this.setCarouselProperty(carousel)
      )
      carousel.$watch('canAdvanceForward', () =>
        this.setCarouselProperty(carousel)
      )
    },
    setCarouselProperty(carousel) {
      this.carousel.canAdvanceBackward = carousel.canAdvanceBackward
      this.carousel.canAdvanceForward = carousel.canAdvanceForward
      return carousel
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

.inactive-group-header {
  header {
    background: map-get($theme-colors, 'gray08') !important;
  }
}

.dashboard-group {
  margin: -1.25rem;

  header {
    .header-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      .header-actions {
        padding: 10px;
        .btn {
          width: 175px !important;
        }
      }
    }

    h2 {
      font-weight: 500;
    }

    small {
      font-size: 14px;
    }

    .edit-btn {
      font-size: 0.95rem;
      text-transform: none !important;
      width: 90px;
      @media (max-width: '991px') {
        width: inherit;
      }
    }
  }
}

.flex-center {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 10px;
}

.collumn-group {
  @media (max-width: '767px') {
    float: none;
    margin: 10px auto;
  }
}

.group-breadcrumb {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;

  > div {
    display: flex;
    color: map-get($theme-colors, 'primary');

    .bread {
      cursor: pointer;
    }
  }
}

.users-list {
  .user-list-wrap {
    border-radius: 8px;

    header {
      align-items: center;
      background: map-get($theme-colors, 'light');
      border-radius: 8px 8px 0 0;
      display: flex;
      padding: 10px 0 10px 20px;
      width: 100%;

      .title-wrap {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        // line-height: 0.5rem;
        margin-left: 15px;

        h2 {
          font-weight: 500;
          margin-bottom: 0;
        }

        small {
          color: map-get($theme-colors, 'septenary');
          font-size: 14px;
        }
      }

      .btn-options {
        align-items: center;
        border-width: 2px;
        display: flex;
        font-size: 22px;
        justify-content: center;
        max-height: 32px;
        padding: 0;
        width: 32px;

        &:before {
          align-items: center;
          display: flex;
          font-size: 24px;
          justify-content: center;
          max-height: 25px;
        }
      }
    }
    .managers {
      align-items: center;
      border-bottom: 1px solid map-get($theme-colors, 'gray07');
      display: flex;
      padding: 0;

      @media (max-width: 767px) {
        flex-wrap: wrap;
      }

      .buttons-wrapper,
      .carousel {
        height: 3em;
      }

      .buttons-wrapper__forward {
        margin-left: auto;
      }

      .carousel {
        &__wrapper {
          width: 55%;

          @include media-breakpoint-up(sm) {
            width: 75%;
          }

          @include media-breakpoint-up(xl) {
            width: 80%;
          }
        }
        &__slide {
          align-items: center;
          border-left: 1px solid map-get($theme-colors, 'gray07');
          // flex-basis: 210px;

          &:first-of-type {
            border-left: none;
          }

          &__icon {
            align-items: center;
            color: map-get($theme-colors, 'primary');
            display: none;
            font-size: 30px;
            margin-top: 10px;

            @include media-breakpoint-up(md) {
              display: inline-block;
            }
          }

          &__text {
            margin-top: 12px;
            color: map-get($theme-colors, 'septenary');
            display: inline-block;
            vertical-align: top;
            height: 37px;
            max-width: 8em;

            @include media-breakpoint-up(sm) {
              max-width: 10em;
            }

            @include media-breakpoint-up(md) {
              max-width: 8em;
            }

            @media screen and (min-width: 1440px) {
              max-width: 12em;
            }
          }
        }
        &__forward,
        &__backward {
          font-size: 30px;
          padding: 0.1em 0;
        }
      }
    }
    .members {
      margin: 0;
      padding: 0;

      .user-item-list {
        align-items: center;
        border-bottom: 1px solid map-get($theme-colors, 'gray07');
        display: flex;
        padding: 10px 15px;

        .image {
          align-items: center;
          color: map-get($theme-colors, 'primary');
          display: flex;
          font-size: 50px;
          height: 50px;
        }

        .user-title {
          margin-left: 20px;
          line-height: 1rem;

          .name {
            color: map-get($theme-colors, 'septenary');
            font-weight: 500;
          }

          .email {
            font-size: 0.8rem;
          }

          .role {
            color: map-get($theme-colors, 'gray03');
            font-weight: 500;
            font-size: 14px;

            span {
              color: map-get($theme-colors, 'green-dark');

              &.invited {
                color: map-get($theme-colors, 'orange');
              }
            }
          }
        }
      }
    }
    .label-inactive {
      div,
      i,
      span {
        color: map-get($theme-colors, 'gray02') !important;
      }
    }
  }
}

/deep/ .tc-popover {
  padding: 0;

  .tc-popover-body {
    margin: 0.25rem 0;
  }

  ul {
    padding: 0;
  }

  li {
    cursor: pointer;
    color: map-get($theme-colors, 'gray03');
    font-weight: 700;
    text-align: right;
    min-width: 250px;
    padding: 0.7rem;
    width: 33%;

    &:hover {
      background-color: map-get($theme-colors, 'gray08');
      color: map-get($theme-colors, 'primary');
    }
  }
}
</style>

<style lang="scss">
.users-list {
  .card-body {
    padding: 0;
  }
}
</style>
